import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Sparkles } from 'lucide-react';
import { AssistantAPI } from '../api';
import { AssistantOrb } from './Brand';
import { ASSISTANT_NAME } from '../config/brand';

const AssistantChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'bot', text: `Hi! I'm ${ASSISTANT_NAME}. How can I help you build today?` }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setLoading(true);
    try {
      const res = await AssistantAPI.chat(text, sessionId);
      if (!sessionId && res.session_id) setSessionId(res.session_id);
      setMessages((m) => [...m, { role: 'bot', text: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'bot', text: "Sorry, I couldn't reach the assistant service. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-6 w-[360px] max-w-[calc(100vw-2rem)] h-[480px] bg-white rounded-2xl shadow-2xl border border-[#E3E5F5] flex flex-col overflow-hidden z-40 animate-in fade-in slide-in-from-bottom-4 duration-200" data-testid="assistant-window">
          <div className="px-4 py-3 border-b border-[#E3E5F5] flex items-center gap-2">
            <AssistantOrb />
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-[#0A1551]">{ASSISTANT_NAME}</div>
              <div className="text-[11px] text-[#0AA24F] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#0AA24F]"></span>Online</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-[#F0F1FB]" aria-label="Close"><X className="w-4 h-4 text-[#6F76A7]" /></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}>
                {m.role === 'bot' && <AssistantOrb />}
                <div className={`px-3 py-2 rounded-2xl max-w-[75%] text-[13px] whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#0A1551] text-white rounded-tr-sm' : 'bg-[#F5F6FB] text-[#0A1551] rounded-tl-sm'}`}>{m.text}</div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-2 items-center">
                <AssistantOrb />
                <div className="px-3 py-2 rounded-2xl bg-[#F5F6FB] flex items-center gap-1.5">
                  {[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 bg-[#B0B4D0] rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }}></span>)}
                </div>
              </div>
            )}
          </div>
          <div className="p-3 border-t border-[#E3E5F5] flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder={`Ask ${ASSISTANT_NAME} anything`} disabled={loading} data-testid="assistant-input"
              className="flex-1 px-3 py-2 border border-[#E3E5F5] rounded-full text-[13px] focus:outline-none focus:border-[#0A1551] focus:ring-2 focus:ring-[#0A1551]/10 disabled:opacity-60" />
            <button onClick={send} disabled={loading || !input.trim()} data-testid="assistant-send" className="w-9 h-9 rounded-full bg-[#0F766E] hover:bg-[#0B5F58] text-white flex items-center justify-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      <button onClick={() => setOpen((v) => !v)} data-testid="assistant-toggle" className="fixed bottom-6 right-6 z-40 bg-white shadow-xl rounded-full pl-1 pr-1 py-1 flex items-center gap-2 border border-[#E3E5F5] hover:shadow-2xl transition-shadow">
        <AssistantOrb size={36} />
        <span className="text-[13px] font-medium text-[#0A1551] px-1">Ask {ASSISTANT_NAME} anything</span>
        <span className="flex items-center gap-1 bg-[#0F766E] text-white rounded-full px-3 py-1.5 text-[12px] font-semibold"><Sparkles className="w-3.5 h-3.5" /> Chat</span>
      </button>
    </>
  );
};

export default AssistantChat;
